import { fonts } from "@/constants/fonts";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ReactNode } from "react";
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import ThemedText from "../core/ThemedText";
import { Button } from "../forms/Button";

interface EmptyStateProps {
  icon?: ReactNode;
  illustration?: ReactNode;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  containerStyle?: StyleProp<ViewStyle>;
}

export default function EmptyState({
  icon,
  illustration,
  title,
  description,
  actionLabel,
  onAction,
  containerStyle,
}: EmptyStateProps) {
  const iconBackgroundColor = useThemeColor({}, "backgroundSecondary");
  const borderColor = useThemeColor({}, "border");

  return (
    <View
      style={[
        styles.container,
        containerStyle,
        { backgroundColor: "transparent" },
      ]}
    >
      {illustration ? (
        <View style={styles.illustrationContainer}>{illustration}</View>
      ) : icon ? (
        <View
          style={[
            styles.iconContainer,
            {
              backgroundColor: iconBackgroundColor,
              borderColor: borderColor,
            },
          ]}
        >
          {icon}
        </View>
      ) : null}

      <View style={styles.textContainer}>
        {title && <ThemedText style={styles.title}>{title}</ThemedText>}

        {description && (
          <ThemedText style={styles.description}>{description}</ThemedText>
        )}
      </View>

      {actionLabel && onAction && (
        <View style={styles.actionContainer}>
          <Button onPress={onAction} size="md" variant="outline">
            <ThemedText style={styles.actionButtonText}>
              {actionLabel}
            </ThemedText>
          </Button>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 32,
    paddingVertical: 48,
  },
  illustrationContainer: {
    marginBottom: 32,
  },
  iconContainer: {
    width: 96,
    height: 96,
    borderRadius: 48,
    borderWidth: 1,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 32,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  textContainer: {
    alignItems: "center",
    maxWidth: 320,
    marginBottom: 8,
  },
  title: {
    fontFamily: fonts.semiBold,
    fontSize: 20,
    textAlign: "center",
    marginBottom: 12,
    lineHeight: 28,
    letterSpacing: -0.3,
  },
  description: {
    fontFamily: fonts.regular,
    fontSize: 15,
    textAlign: "center",
    opacity: 0.7,
    lineHeight: 22,
    letterSpacing: -0.1,
  },
  actionContainer: {
    marginTop: 24,
    minWidth: 160,
  },
  actionButtonText: {
    fontFamily: fonts.medium,
    fontSize: 15,
    letterSpacing: -0.1,
  },
});
